import { useState } from 'react';
import { Upload, X, CheckCircle2, AlertCircle, Link, MessageCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTranslation } from '../hooks/useTranslation';

export function UploadForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    appType: 'android',
    description: ''
  });
  const [requestType, setRequestType] = useState<'upload' | 'contact'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<'file' | 'link'>('file');
  const [driveLink, setDriveLink] = useState('');
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.size > 100 * 1024 * 1024) {
        setError('File quá lớn. Vui lòng chọn file dưới 100MB');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (requestType === 'upload') {
      if (uploadType === 'file' && !file) {
        setError('Vui lòng chọn file source code');
        return;
      }

      if (uploadType === 'link' && !driveLink.trim()) {
        setError('Vui lòng nhập link Drive/OneDrive');
        return;
      }
    }

    setUploading(true);
    setError('');

    try {
      if (requestType === 'contact') {
        // Chỉ gửi email thông báo, không lưu vào database
        await sendContactNotification();
      } else {
        // Upload source code như cũ
        let filePath = '';
        let fileName = '';
        let fileSize = 0;

        if (uploadType === 'file' && file) {
          fileName = `${Date.now()}_${formData.email}_${file.name}`;
          filePath = `submissions/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('source-code')
            .upload(filePath, file);

          if (uploadError) throw uploadError;
          fileSize = file.size;
        } else {
          // Store link as file_path for consistency
          fileName = `link_${Date.now()}_${formData.email}`;
          filePath = driveLink;
          fileSize = 0;
        }

        const { error: dbError } = await supabase
          .from('submissions')
          .insert([
            {
              name: formData.name,
              email: formData.email,
              phone: formData.phone,
              app_type: formData.appType,
              description: formData.description,
              file_path: filePath,
              file_name: fileName,
              file_size: fileSize,
              upload_type: uploadType,
              drive_link: uploadType === 'link' ? driveLink : null
            }
          ]);

        if (dbError) throw dbError;

        // Send email notification
        await sendEmailNotification();
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Có lỗi xảy ra. Vui lòng thử lại.';
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const sendEmailNotification = async () => {
    try {
      // Prepare email content
      const subject = `Yêu cầu triển khai ứng dụng từ ${formData.name}`;
      const body = `
Yêu cầu triển khai ứng dụng mới

Thông tin khách hàng:
- Họ tên: ${formData.name}
- Email: ${formData.email}
- Số điện thoại: ${formData.phone}
- Loại ứng dụng: ${formData.appType}
- Mô tả: ${formData.description}
- Phương thức upload: ${uploadType === 'file' ? 'Upload file' : 'Link Drive'}
${uploadType === 'link' ? `- Link Drive: ${driveLink}` : ''}
- Thời gian: ${new Date().toLocaleString('vi-VN')}

Vui lòng kiểm tra và liên hệ lại khách hàng sớm nhất.

---
Gửi từ DeployPro Website
      `;

      // Create mailto link
      const mailtoLink = `mailto:hqminh050503@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      
      // Try to open email client
      window.open(mailtoLink, '_blank');
      
      // Also log to console for backup
      console.log('Email notification sent via mailto');
      console.log('To: hqminh050503@gmail.com');
      console.log('Subject:', subject);
      console.log('Body:', body);
      
    } catch (error) {
      console.error('Email notification error:', error);
      
      // Fallback: log email content for manual sending
      console.log('Email content for manual sending:');
      console.log('To: hqminh050503@gmail.com');
      console.log('Subject: Yêu cầu triển khai ứng dụng từ', formData.name);
      console.log('Body:', {
        'Họ tên': formData.name,
        'Email': formData.email,
        'Số điện thoại': formData.phone,
        'Loại ứng dụng': formData.appType,
        'Mô tả': formData.description,
        'Phương thức upload': uploadType === 'file' ? 'Upload file' : 'Link Drive',
        'Link Drive': uploadType === 'link' ? driveLink : '',
        'Thời gian': new Date().toLocaleString('vi-VN')
      });
    }
  };

  const sendContactNotification = async () => {
    try {
      // Prepare email content for contact request
      const subject = `Yêu cầu liên hệ hỗ trợ từ ${formData.name}`;
      const body = `
Yêu cầu liên hệ hỗ trợ mới

Thông tin khách hàng:
- Họ tên: ${formData.name}
- Email: ${formData.email}
- Số điện thoại: ${formData.phone}
- Loại ứng dụng: ${formData.appType}
- Mô tả: ${formData.description}
- Thời gian: ${new Date().toLocaleString('vi-VN')}

Khách hàng muốn liên hệ hỗ trợ qua Zalo hoặc Facebook Messenger.

Liên hệ khách hàng:
- Email: ${formData.email}
- Số điện thoại: ${formData.phone}
- Facebook: https://www.facebook.com/profile.php?id=61577741002396

---
Gửi từ DeployPro Website
      `;

      // Create mailto link
      const mailtoLink = `mailto:hqminh050503@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      
      // Try to open email client
      window.open(mailtoLink, '_blank');
      
      // Also log to console for backup
      console.log('Contact notification sent via mailto');
      console.log('To: hqminh050503@gmail.com');
      console.log('Subject:', subject);
      console.log('Body:', body);
      
    } catch (error) {
      console.error('Contact notification error:', error);
      
      // Fallback: log email content for manual sending
      console.log('Contact content for manual sending:');
      console.log('To: hqminh050503@gmail.com');
      console.log('Subject: Yêu cầu liên hệ hỗ trợ từ', formData.name);
      console.log('Body:', {
        'Họ tên': formData.name,
        'Email': formData.email,
        'Số điện thoại': formData.phone,
        'Loại ứng dụng': formData.appType,
        'Mô tả': formData.description,
        'Thời gian': new Date().toLocaleString('vi-VN'),
        'Facebook': 'https://www.facebook.com/profile.php?id=61577741002396'
      });
    }
  };

  const openZalo = () => {
    const message = `Xin chào! Tôi là ${formData.name}. Tôi muốn được hỗ trợ về dịch vụ triển khai ứng dụng.`;
    const zaloUrl = `https://zalo.me/0961801543?text=${encodeURIComponent(message)}`;
    window.open(zaloUrl, '_blank');
  };

  const openFacebook = () => {
    const facebookUrl = 'https://www.facebook.com/profile.php?id=61577741002396';
    window.open(facebookUrl, '_blank');
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <h3 className="text-xl font-bold text-slate-900 mb-2">Thành công!</h3>
          <p className="text-slate-600 text-sm">
            Chúng tôi đã nhận được source code của bạn. Chúng tôi sẽ liên hệ lại sớm nhất.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl p-4 max-w-lg w-full my-4">
        <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-bold text-slate-900">
                    {requestType === 'upload' ? t('form.upload_title') : t('form.contact_title')}
                  </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Request Type Toggle */}
        <div className="flex rounded-lg border border-slate-300 mb-3 overflow-hidden">
          <button
            type="button"
            onClick={() => setRequestType('upload')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
              requestType === 'upload'
                ? 'bg-emerald-500 text-white'
                : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <Upload className="w-4 h-4" />
                    <span>{t('form.upload_source')}</span>
          </button>
          <button
            type="button"
            onClick={() => setRequestType('contact')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
              requestType === 'contact'
                ? 'bg-emerald-500 text-white'
                : 'bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            <MessageCircle className="w-4 h-4" />
                    <span>{t('form.contact_support')}</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('form.full_name')} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder={t('form.name_placeholder')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('form.email')} <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder={t('form.email_placeholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('form.phone')} <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder={t('form.phone_placeholder')}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('form.app_type')} <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={formData.appType}
              onChange={(e) => setFormData({ ...formData, appType: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="android">{t('form.android')}</option>
              <option value="ios">{t('form.ios')}</option>
              <option value="web">{t('form.website')}</option>
              <option value="both">{t('form.both')}</option>
              <option value="all">{t('form.all')}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('form.description')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder={t('form.description_placeholder')}
            />
          </div>

          {/* Source Code Upload Section - Only show for upload type */}
          {requestType === 'upload' && (
            <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('form.source_code')} <span className="text-red-500">*</span>
            </label>
              
              {/* Upload Type Toggle */}
              <div className="flex rounded-lg border border-slate-300 mb-3 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setUploadType('file')}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                    uploadType === 'file'
                      ? 'bg-emerald-500 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                        <Upload className="w-4 h-4 inline mr-2" />
                        {t('form.upload_file')}
                </button>
                <button
                  type="button"
                  onClick={() => setUploadType('link')}
                  className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                    uploadType === 'link'
                      ? 'bg-emerald-500 text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                        <Link className="w-4 h-4 inline mr-2" />
                        {t('form.link_drive')}
                </button>
              </div>

              {/* File Upload */}
              {uploadType === 'file' && (
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-emerald-500 transition-colors">
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".zip,.rar,.7z,.tar,.gz"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                            <span className="text-slate-600 mb-1">
                              {file ? file.name : t('form.file_drop_text')}
                            </span>
                            <span className="text-sm text-slate-500">
                              {t('form.file_support')}
                            </span>
                  </label>
                </div>
              )}

              {/* Drive Link Input */}
              {uploadType === 'link' && (
                <div className="space-y-3">
                  <input
                    type="url"
                    value={driveLink}
                    onChange={(e) => setDriveLink(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder={t('form.drive_placeholder')}
                  />
                  <div className="text-sm text-slate-500">
                    <p className="mb-1">Hỗ trợ các dịch vụ:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Google Drive (chia sẻ công khai hoặc có quyền truy cập)</li>
                      <li>OneDrive (chia sẻ công khai)</li>
                      <li>Dropbox (chia sẻ công khai)</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Options Section - Only show for contact type */}
          {requestType === 'contact' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('form.contact_channel')} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={openZalo}
                  className="flex items-center justify-center space-x-2 px-3 py-2 border border-slate-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-3 h-3 text-white" />
                  </div>
                  <span className="font-medium">{t('form.chat_zalo')}</span>
                </button>
                <button
                  type="button"
                  onClick={openFacebook}
                  className="flex items-center justify-center space-x-2 px-3 py-2 border border-slate-300 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-colors"
                >
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-3 h-3 text-white" />
                  </div>
                  <span className="font-medium">{t('form.facebook')}</span>
                </button>
              </div>
              <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-800">
                  <strong>{t('form.note')}:</strong> {t('form.contact_note')}
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-2 rounded-lg">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={uploading}
            className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading 
              ? (requestType === 'upload' ? t('form.uploading') : t('form.sending')) 
              : (requestType === 'upload' ? t('form.submit_upload') : t('form.submit_contact'))
            }
          </button>
        </form>
      </div>
    </div>
  );
}
